/**
 * iter302 — Régularisation comptable TDFN (Comptabilité → TVA).
 *
 * Simulation d'abord (rien n'est écrit), exécution ensuite. La régularisation n'ajoute
 * que des écritures : rollback complet en un clic.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Scale, Undo2, Play, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (n) => (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Ligne = ({ label, avant, apres }) => (
  <tr className="border-t border-gray-100">
    <td className="px-3 py-1.5 text-gray-700">{label}</td>
    <td className="px-3 py-1.5 text-right font-mono text-xs text-gray-500">{fmt(avant)}</td>
    <td className="px-3 py-1.5 text-right font-mono text-xs font-semibold">{fmt(apres)}</td>
  </tr>
);

const TdfnRegularisationCard = ({ role }) => {
  const [sim, setSim] = useState(null);
  const [migrations, setMigrations] = useState([]);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/tva/migration");
      setMigrations(data || []);
    } catch { /* lecture seule */ }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const simuler = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tva/migration/simulation");
      setSim(data);
      toast.success("Simulation calculée — rien n'a été écrit");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const executer = async () => {
    if (!window.confirm(
      "Exécuter la régularisation TDFN ?\n\n"
      + `${sim.ecritures_a_creer} écritures seront AJOUTÉES (aucune écriture existante modifiée).\n`
      + `Solde 2200 : ${fmt(sim.avant.solde_2200)} → ${fmt(sim.apres.solde_2200)}\n`
      + `Résultat : ${fmt(sim.avant.resultat)} → ${fmt(sim.apres.resultat)}\n\n`
      + "Annulable à tout moment.",
    )) return;
    setBusy(true);
    try {
      await api.post("/tva/migration/executer", { confirmer: true });
      toast.success("Régularisation exécutée");
      setSim(null);
      charger();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const annuler = async (m) => {
    if (!window.confirm("Annuler la régularisation ?\n\nLes écritures ajoutées seront supprimées et les soldes reviendront à leur état précédent.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/tva/migration/${m.id}?confirmer=true`);
      toast.success(`${data.ecritures_supprimees} écriture(s) supprimée(s)`);
      charger();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const active = migrations.find((m) => !m.annule);
  if (role !== "admin") return null;

  return (
    <Card className="p-4" data-testid="tdfn-regularisation-card">
      <div className="flex items-center gap-2 mb-1">
        <Scale className="w-4 h-4 text-[#52a675]" />
        <h3 className="text-base font-semibold text-gray-900">Régularisation comptable TDFN</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Reclasse la TVA facturée historiquement portée au 2200 en produit (3891) et comptabilise
        la dette TDFN réelle par mois (3892). Uniquement des écritures ajoutées : factures,
        créances, paiements et décomptes clôturés restent intacts.
      </p>

      {active ? (
        <div className="p-3 rounded-lg border border-[#52a675]/40 bg-[#52a675]/5 text-xs" data-testid="tdfn-regularisation-active">
          <div className="font-semibold text-gray-900">
            Régularisation exécutée le {String(active.execute_le).slice(0, 10).split("-").reverse().join(".")}
            {active.execute_par_nom ? ` par ${active.execute_par_nom}` : ""}
          </div>
          <div className="text-gray-600 mt-0.5">
            {active.ecritures_creees?.length} écritures ajoutées · solde 2200 :{" "}
            {fmt(active.situation_apres_reelle?.solde_2200)} CHF
          </div>
          <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => annuler(active)} data-testid="tdfn-annuler-migration">
            <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Annuler cette régularisation
          </Button>
        </div>
      ) : (
        <>
          <Button size="sm" variant="outline" onClick={simuler} disabled={busy} data-testid="tdfn-simuler">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
            Simuler (rien n&apos;est écrit)
          </Button>

          {sim && (
            <div className="mt-3 space-y-2" data-testid="tdfn-simulation">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-3 pb-1 text-left font-medium"></th>
                    <th className="px-3 pb-1 text-right font-medium">Avant</th>
                    <th className="px-3 pb-1 text-right font-medium">Après</th>
                  </tr>
                </thead>
                <tbody>
                  <Ligne label="Chiffre d'affaires" avant={sim.avant.ca_produits} apres={sim.apres.ca_produits} />
                  <Ligne label="Résultat" avant={sim.avant.resultat} apres={sim.apres.resultat} />
                  <Ligne label="Solde 2200 (dette TVA)" avant={sim.avant.solde_2200} apres={sim.apres.solde_2200} />
                  <Ligne label="Total actif (bilan)" avant={sim.avant.total_actif} apres={sim.apres.total_actif} />
                  <Ligne label="Écritures au journal" avant={sim.avant.ecritures} apres={sim.apres.ecritures} />
                </tbody>
              </table>
              <div className="text-xs text-gray-600 px-3 space-y-0.5">
                <div>TVA facturée reclassée : <b>{fmt(sim.tva_facturee_reclassee)}</b> CHF sur {sim.mois_concernes} mois</div>
                <div>Dette TDFN réelle : <b>{fmt(sim.dette_tdfn_totale)}</b> CHF · versements AFC déjà comptabilisés : {fmt(sim.paiements_afc_deja_comptabilises)} CHF</div>
                <div>Reste à payer à l&apos;AFC : <b>{fmt(sim.dette_tdfn_restant_a_payer)}</b> CHF</div>
                <div>{sim.ecritures_a_creer} écriture(s) créée(s) · {sim.ecritures_a_modifier} modifiée(s)</div>
                <div className="text-gray-400">
                  Décomptes de contrôle (identiques après) :{" "}
                  {Object.entries(sim.decomptes_tva_controle || {}).map(([a, v]) => `${a} : ${fmt(v.tva_due)}`).join(" · ")}
                </div>
              </div>
              <Button size="sm" onClick={executer} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c] ml-3" data-testid="tdfn-executer">
                <Play className="w-3.5 h-3.5 mr-1.5" /> Exécuter la régularisation
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default TdfnRegularisationCard;
